import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import { motion } from 'framer-motion';
import { API_BASE, getImageUrl } from '../utils/api';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

interface HeroBanner {
    _id: string;
    type: 'image' | 'video';
    url: string;
    title?: string;
    subtitle?: string;
    link?: string;
    isActive: boolean;
}

const Typewriter = ({ text }: { text: string }) => {
    const [displayedText, setDisplayedText] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [charIndex, setCharIndex] = useState(0);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!isDeleting && charIndex < text.length) {
                setDisplayedText(text.substring(0, charIndex + 1));
                setCharIndex(prev => prev + 1);
            } else if (isDeleting && charIndex > 0) {
                setDisplayedText(text.substring(0, charIndex - 1));
                setCharIndex(prev => prev - 1);
            } else if (charIndex === text.length) {
                setTimeout(() => setIsDeleting(true), 2500);
            } else if (charIndex === 0 && isDeleting) {
                setIsDeleting(false);
            }
        }, isDeleting ? 50 : 100);
        return () => clearTimeout(timeout);
    }, [charIndex, isDeleting, text]);

    return (
        <span className="min-h-[1.5em] inline-block">
            {displayedText}
            <span className="inline-block w-[2px] h-[1em] bg-white/60 ml-1 animate-pulse align-middle" />
        </span>
    );
};

const HeroSlider = () => {
    const [banners, setBanners] = useState<HeroBanner[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API_BASE}/hero-banners`)
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data) && data.length > 0) {
                    setBanners(data);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching hero banners:", err);
                setLoading(false);
            });
    }, []);

    const settings = {
        dots: true,
        infinite: true,
        speed: 1000,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        fade: false,
        arrows: false,
        dotsClass: "slick-dots custom-dots"
    };

    if (loading) {
        return <div className="min-h-[550px] bg-white animate-pulse"></div>;
    }

    // If only one banner exists, render it directly without the Slider
    if (banners.length === 1) {
        const banner = banners[0];
        const bannerContent = (
            <section className="relative w-full flex items-center justify-center h-[38vw] min-h-[240px] md:min-h-[490px] overflow-hidden pt-2 pb-8 md:pt-[120px] md:pb-[140px] mx-auto">
                {banner.type === 'image' ? (
                    <div className="absolute inset-0 z-0 flex items-center justify-center">
                        <img src={getImageUrl(banner.url)} alt="" className="w-full h-full object-cover object-center" />
                    </div>
                ) : (
                    <video
                        src={getImageUrl(banner.url)}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover object-center"
                    />
                )}
                
                <div className="container relative z-10 mx-auto px-4">
                    <div className="flex flex-wrap justify-center text-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8 }}
                            className="w-full"
                        >
                            {banner.subtitle && (
                                <p className="text-[10px] md:text-[0.95rem] mb-1 font-medium tracking-wider text-white uppercase italic">
                                    <Typewriter text={banner.subtitle} />
                                </p>
                            )}
                            <h1 className="text-lg lg:text-7xl md:text-5xl font-black leading-tight text-white drop-shadow-2xl px-2 mt-6">
                                {banner.title}
                            </h1>
                        </motion.div>
                    </div>
                </div>
            </section>
        );

        return banner.link ? (
            <a href={banner.link} className="block cursor-pointer">
                {bannerContent}
            </a>
        ) : bannerContent;
    }

    // Default static banner if no banners are configured
    if (banners.length === 0) {
        return (
            <section className="relative w-full flex items-center justify-center bg-cover bg-center bg-no-repeat h-[33.33vw] min-h-[220px] md:min-h-[450px] pt-[120px] pb-[140px] mx-auto"
                style={{ backgroundImage: "url('/img/hero-banner1.png')" }}>
                <div className="container relative z-10 mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                    >
                        <p className="text-[10px] md:text-[0.95rem] mb-1 font-medium tracking-wider text-white italic">
                            <Typewriter text="Establishing Excellence" />
                        </p>
                    </motion.div>
                </div>
            </section>
        );
    }

    return (
        <div className="hero-slider-wrapper w-full max-w-full overflow-hidden relative">
            <Slider {...settings}>
                {banners.map((banner) => {
                    const slideContent = (
                        <section className="relative flex items-center justify-center bg-cover bg-center bg-no-repeat w-full h-[38vw] min-h-[200px] pt-1 pb-10 md:pt-[50px] md:pb-[100px] md:min-h-[430px] mx-auto">
                            {banner.type === 'image' ? (
                                <div className="absolute inset-0 z-0 flex items-center justify-center">
                                    <img src={getImageUrl(banner.url)} alt="" className="w-full h-full object-cover object-center" />
                                </div>
                            ) : (
                                <div className="absolute inset-0 z-0 text-center flex items-center justify-center">
                                    <video src={getImageUrl(banner.url)} autoPlay loop muted playsInline className="w-full h-full object-cover object-center" />
                                </div>
                            )}

                            <div className="container relative z-10 mx-auto px-4 text-center">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8 }}
                                >
                                    {banner.subtitle && (
                                        <p className="text-[10px] md:text-[0.95rem] mb-1 font-medium tracking-wider text-white uppercase italic">
                                            <Typewriter text={banner.subtitle} />
                                        </p>
                                    )}
                                    <h1 className="text-lg lg:text-7xl md:text-5xl font-black leading-[1.1] text-white drop-shadow-2xl px-2 mt-6">
                                        {banner.title}
                                    </h1>
                                </motion.div>
                            </div>
                        </section>
                    );

                    return (
                        <div key={banner._id} className="relative w-full mx-auto overflow-hidden">
                            {banner.link ? (
                                <a href={banner.link} className="block cursor-pointer">
                                    {slideContent}
                                </a>
                            ) : slideContent}
                        </div>
                    );
                })}
            </Slider>
            <style>{`
                .hero-slider-wrapper .slick-slider {
                    width: 100% !important;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                .hero-slider-wrapper .slick-list,
                .hero-slider-wrapper .slick-track {
                    margin: 0 !important;
                    padding: 0 !important;
                }
                .hero-slider-wrapper .slick-slide {
                    padding: 0 !important;
                    margin: 0 !important;
                }
                .hero-slider-wrapper .slick-slide > div {
                    display: block;
                    width: 100% !important;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                .custom-dots {
                    bottom: 60px;
                }
                @media (max-width: 768px) {
                    .custom-dots {
                        display: none !important;
                    }
                }
                .custom-dots li button:before {
                    color: white;
                    font-size: 12px;
                    opacity: 0.5;
                }
                .custom-dots li.slick-active button:before {
                    color: #c88128;
                    opacity: 1;
                }
            `}</style>
        </div>
    );
};

export default HeroSlider;